import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { brandReportService } from "../api/services/brandReportService";
import type {
  BrandReport,
  BrandReportCreativeOption,
  BrandReportFilter,
  BrandReportScreenRow,
  BrandReportScreensPage,
} from "../schemas/brandReportSchema";
import {
  demoBrandReport,
  demoBrandReportCreatives,
  demoBrandReportScreens,
  isBrandDemoData,
} from "../mocks/brandReportDemo";

const SCREENS_PAGE_SIZE = 25;

export const useBrandReport = (filter: BrandReportFilter) => {
  const query = useQuery<BrandReport, Error>({
    queryKey: ["brand-report", filter, isBrandDemoData()],
    queryFn: () =>
      isBrandDemoData()
        ? Promise.resolve(demoBrandReport(filter))
        : brandReportService.getReport(filter),
  });

  return {
    report: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
};

/** Per-screen proof of play, loaded a page at a time as the table scrolls. */
export const useBrandReportScreens = (
  filter: BrandReportFilter,
  search: string,
) => {
  const query = useInfiniteQuery<BrandReportScreensPage, Error>({
    queryKey: ["brand-report-screens", filter, search, isBrandDemoData()],
    queryFn: ({ pageParam }) => {
      const params = {
        search: search || undefined,
        limit: SCREENS_PAGE_SIZE,
        offset: pageParam as number,
      };
      return isBrandDemoData()
        ? Promise.resolve(demoBrandReportScreens(filter, params))
        : brandReportService.getScreens(filter, params);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.totalCount ? loaded : undefined;
    },
  });

  const screens: BrandReportScreenRow[] =
    query.data?.pages.flatMap((page) => page.items) ?? [];

  return {
    screens,
    total: query.data?.pages[0]?.totalCount ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};

export const useBrandReportCreatives = () => {
  const query = useQuery<BrandReportCreativeOption[], Error>({
    queryKey: ["brand-report-creatives", isBrandDemoData()],
    queryFn: () =>
      isBrandDemoData()
        ? Promise.resolve(demoBrandReportCreatives())
        : brandReportService.getCreatives(),
    staleTime: 5 * 60 * 1000,
  });
  return { creatives: query.data ?? [], isLoading: query.isLoading };
};
